import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { FileUp, CheckCircle2, AlertCircle, Sparkles, RefreshCw, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import PageHero from "@/components/site/PageHero";
import { analyzeResumeText } from "@/lib/resumeCheck";
import { useSEO } from "@/lib/seo";
import { toast } from "sonner";

async function extractPdfText(file) {
  const pdfjs = await import("pdfjs-dist");
  // pdf.js worker via CDN matched to installed version
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;
  const buf = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: buf }).promise;
  let text = "";
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map((x) => x.str).join(" ") + "\n";
  }
  return text;
}

async function extractDocxText(file) {
  const mammoth = await import("mammoth");
  const buf = await file.arrayBuffer();
  const res = await mammoth.extractRawText({ arrayBuffer: buf });
  return res.value || "";
}

export default function ResumeHealthCheck() {
  useSEO({
    title: "Resume Health Check",
    description: "Instantly score your pharma resume against the 10 criteria recruiters actually evaluate. Free, private, runs in your browser.",
    pageKey: "resume-health-check",
  });

  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const onPick = async (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
    setLoading(true);
    try {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      let text = "";
      if (ext === "pdf") text = await extractPdfText(f);
      else if (ext === "docx") text = await extractDocxText(f);
      else if (ext === "txt") text = await f.text();
      else {
        toast.error("Please upload a PDF, DOCX or TXT file");
        setLoading(false);
        return;
      }
      const r = analyzeResumeText(text);
      setResult(r);
    } catch (err) {
      console.error(err);
      toast.error("Could not read the file. If it's a scanned PDF, please use a text-based PDF instead.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setFile(null); setResult(null); if (fileRef.current) fileRef.current.value = ""; };

  return (
    <>
      <PageHero
        overline="Free · Private · Instant"
        title="Resume Health Check"
        description="Drop your resume — we'll score it against the 10 criteria pharma recruiters actually use. Everything runs in your browser, your file never leaves your device."
      />

      <section className="section-pad">
        <div className="container-page grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="sticky top-24 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="rhc-dropzone">
              <Sparkles className="mx-auto h-10 w-10 text-secondary" />
              <h3 className="mt-4 font-heading text-xl font-semibold text-slate-900">Upload your resume</h3>
              <p className="mt-1 text-sm text-slate-600">PDF, DOCX or TXT · runs locally · no upload to server</p>
              <label htmlFor="rhc-file" className="mt-5 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary/90" data-testid="rhc-file-label">
                <FileUp className="h-4 w-4" /> {file ? "Replace file" : "Choose file"}
              </label>
              <input id="rhc-file" ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} data-testid="rhc-file-input" />
              {file && <div className="mt-3 text-xs text-slate-500">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
              {loading && <div className="mt-3 text-sm text-slate-600">Analyzing…</div>}
              {result && (
                <div className="mt-6 space-y-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Overall score</div>
                  <div className={`font-heading text-5xl font-bold ${result.score >= 80 ? "text-emerald-600" : result.score >= 60 ? "text-amber-600" : "text-rose-600"}`} data-testid="rhc-score">{result.score}</div>
                  <Progress value={result.score} className="h-2" />
                  <div className="pt-2 text-xs text-slate-500">{result.word_count.toLocaleString()} words analysed</div>
                  <div className="flex justify-center gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={reset} data-testid="rhc-reset"><RefreshCw className="mr-1 h-3 w-3" />Try another</Button>
                    <Link to="/upload-resume"><Button size="sm" className="bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="rhc-cta-upload">Submit to recruiters <ArrowRight className="ml-1 h-3 w-3" /></Button></Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            {!result ? (
              <div className="rounded-xl border border-slate-200 bg-white p-8">
                <h3 className="font-heading text-lg font-semibold text-slate-900">What we check</h3>
                <ul className="mt-4 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
                  {[
                    "Contact info present",
                    "LinkedIn linked",
                    "Summary / profile section",
                    "Core sections present",
                    "Right length (1-2 pages)",
                    "Quantified achievements",
                    "Strong action verbs",
                    "Bullet-pointed experience",
                    "Dates on roles",
                    "Pharma-domain keywords",
                  ].map((c) => (
                    <li key={c} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{c}</li>
                  ))}
                </ul>
                <p className="mt-6 text-xs text-slate-500">
                  Tip: works best with text-based PDFs (not scanned images). If your resume is a scan, export from Word/Docs again.
                </p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="rhc-results">
                {result.checks.map((c) => (
                  <div key={c.key} className={`rounded-xl border p-4 ${c.pass ? "border-emerald-200 bg-emerald-50/50" : "border-rose-200 bg-rose-50/50"}`} data-testid={`rhc-check-${c.key}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        {c.pass ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" /> : <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />}
                        <div>
                          <div className="font-medium text-slate-900">{c.label}</div>
                          {!c.pass && <div className="mt-1 text-sm text-slate-600">{c.hint}</div>}
                        </div>
                      </div>
                      <Badge variant="secondary" className={c.pass ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}>+{c.weight}</Badge>
                    </div>
                  </div>
                ))}

                {result.matched.pharma_keywords.length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="text-xs uppercase tracking-wider text-slate-500">Pharma keywords detected</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {result.matched.pharma_keywords.map((k) => <Badge key={k} variant="secondary" className="bg-primary/10 text-primary">{k}</Badge>)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
